import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tv, Eye, Clock, Plus, Trash2, Check, Heart, SortAsc, SortDesc } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getUserSeries, deleteSeries, getImageUrl, getTvdbLogo } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const SeriesCard = ({ series, onDelete }) => {
  const poster = getImageUrl(series.poster_path, 'w342');
  const progress = series.progress_percent || 0;
  const isCompleted = progress >= 100;
  const [logoUrl, setLogoUrl] = useState(series.logo_path);
  const [logoError, setLogoError] = useState(false);

  // Fetch TVDB logo if not available in the database
  useEffect(() => {
    const fetchLogo = async () => {
      if (!series.logo_path && series.title) {
        try {
          const logo = await getTvdbLogo(series.title);
          if (logo) {
            setLogoUrl(logo);
          }
        } catch (e) {
          console.log('Could not fetch logo for', series.title);
        }
      }
    };
    fetchLogo();
  }, [series.logo_path, series.title]);

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await onDelete(series.id);
      toast.success('Serie verwijderd');
    } catch (error) {
      toast.error('Kon serie niet verwijderen');
    }
  };

  return (
    <Link 
      to={`/tv/${series.tmdb_id}`}
      className="group relative rounded-xl overflow-hidden bg-white/5 border border-white/5 hover:border-indigo-500/30 transition-all"
      data-testid={`series-card-${series.tmdb_id}`}
    >
      <div className="flex">
        {/* Poster - Larger */}
        <div className="w-28 md:w-36 flex-shrink-0">
          {poster ? (
            <img src={poster} alt={series.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-44 flex items-center justify-center bg-gray-800">
              <Tv className="w-8 h-8 text-gray-600" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              {/* Logo or Title */}
              {logoUrl && !logoError ? (
                <img 
                  src={logoUrl} 
                  alt={series.title}
                  className="h-10 md:h-12 max-w-full object-contain mb-2"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <h3 className="font-semibold text-white line-clamp-1">{series.title}</h3>
              )}
              
              <div className="flex items-center gap-2 text-sm text-gray-400 mt-1">
                <span>{series.episodes_watched || 0}/{series.total_episodes} afleveringen</span>
              </div>
            </div>

            {/* Status Badge */}
            {isCompleted && (
              <div className="bg-green-500 rounded-full p-1.5 flex-shrink-0">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-400">Voortgang</span>
              <span className={`font-medium ${isCompleted ? 'text-green-400' : 'text-indigo-400'}`}>
                {progress}%
              </span>
            </div>
            <Progress 
              value={progress} 
              className={`h-2 ${isCompleted ? '[&>div]:bg-green-500' : ''}`}
            />
          </div>

          {/* Status & Episodes to watch */}
          <div className="flex items-center justify-between">
            <span className={`text-xs px-2 py-1 rounded-full ${
              series.status === 'watching' ? 'bg-indigo-500/20 text-indigo-300' :
              series.status === 'completed' ? 'bg-green-500/20 text-green-300' :
              series.status === 'watchlist' ? 'bg-pink-500/20 text-pink-300' :
              'bg-red-500/20 text-red-300'
            }`}>
              {series.status === 'watching' ? 'Aan het kijken' :
               series.status === 'completed' ? 'Voltooid' :
               series.status === 'watchlist' ? 'Watchlist' : 'Gestopt'}
            </span>
            {!isCompleted && series.total_episodes - (series.episodes_watched || 0) > 0 && (
              <span className="text-xs text-gray-500">
                Nog {series.total_episodes - (series.episodes_watched || 0)} te kijken
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Delete Button (hover) */}
      <Button
        size="sm"
        variant="destructive"
        onClick={handleDelete}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Trash2 className="w-3 h-3" />
      </Button>
    </Link>
  );
};

// Local storage keys for persisting user preferences
const SERIES_SORT_KEY = 'episo_series_sort';
const SERIES_SORT_DIR_KEY = 'episo_series_sort_dir';

export const SeriesPage = () => {
  const { isAuthenticated } = useAuth();
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem(SERIES_SORT_KEY) || 'progress');
  const [sortDir, setSortDir] = useState(() => localStorage.getItem(SERIES_SORT_DIR_KEY) || 'desc');

  // Persist sort preferences
  useEffect(() => {
    localStorage.setItem(SERIES_SORT_KEY, sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem(SERIES_SORT_DIR_KEY, sortDir);
  }, [sortDir]);

  const fetchSeries = async () => {
    try {
      const data = await getUserSeries();
      setSeries(data.series || []);
    } catch (error) {
      console.error('Error fetching series:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSeries();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const handleDelete = async (seriesId) => {
    await deleteSeries(seriesId);
    setSeries(prev => prev.filter(s => s.id !== seriesId));
  };

  const filteredSeries = series.filter(s => {
    if (activeTab === 'all') return true;
    if (activeTab === 'watching') return s.status === 'watching';
    if (activeTab === 'watchlist') return s.status === 'watchlist';
    if (activeTab === 'completed') return s.progress_percent >= 100 || s.status === 'completed';
    return true;
  });

  // Sort series with direction support
  const sortedSeries = [...filteredSeries].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'progress':
        // Watching first, then by progress
        if (a.status === 'watching' && b.status !== 'watching') return -1;
        if (b.status === 'watching' && a.status !== 'watching') return 1;
        comparison = (b.progress_percent || 0) - (a.progress_percent || 0);
        break;
      case 'added_at':
        comparison = new Date(b.added_at) - new Date(a.added_at);
        break;
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'episodes_left':
        const aLeft = (a.total_episodes || 0) - (a.episodes_watched || 0);
        const bLeft = (b.total_episodes || 0) - (b.episodes_watched || 0);
        comparison = aLeft - bLeft;
        break;
      default:
        comparison = 0;
    }
    return sortDir === 'asc' ? -comparison : comparison;
  });

  const watchingCount = series.filter(s => s.status === 'watching').length;
  const watchlistCount = series.filter(s => s.status === 'watchlist').length;
  const completedCount = series.filter(s => s.progress_percent >= 100 || s.status === 'completed').length;
  const totalEpisodesWatched = series.reduce((sum, s) => sum + (s.episodes_watched || 0), 0);

  if (!isAuthenticated) {
    return (
      <div data-testid="series-page" className="min-h-screen p-6 md:p-12">
        <div className="max-w-2xl mx-auto text-center py-20">
          <Tv className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h1 className="text-2xl font-display font-bold mb-4">Mijn Series</h1>
          <p className="text-gray-400 mb-8">Log in om je seriebibliotheek te bekijken en beheren.</p>
          <Link to="/login">
            <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8 py-6">
              Inloggen
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="series-page" className="min-h-screen p-6 md:p-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-display font-bold mb-2">Mijn Series</h1>
        <p className="text-gray-400">
          {watchingCount} aan het kijken • {completedCount} voltooid • {watchlistCount} op watchlist
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-2">
            <Eye className="w-5 h-5 text-indigo-400" />
            <span className="text-gray-400 text-sm">Aan het kijken</span>
          </div>
          <p className="text-2xl font-display font-bold">{watchingCount}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-2">
            <Check className="w-5 h-5 text-green-400" />
            <span className="text-gray-400 text-sm">Voltooid</span>
          </div>
          <p className="text-2xl font-display font-bold">{completedCount}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-purple-400" />
            <span className="text-gray-400 text-sm">Afleveringen</span>
          </div>
          <p className="text-2xl font-display font-bold">{totalEpisodesWatched}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-5 h-5 text-pink-400" />
            <span className="text-gray-400 text-sm">Watchlist</span>
          </div>
          <p className="text-2xl font-display font-bold">{watchlistCount}</p>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger 
              value="all" 
              data-testid="tab-all"
              className="data-[state=active]:bg-indigo-600"
            >
              Alles ({series.length})
            </TabsTrigger>
            <TabsTrigger 
              value="watching" 
              data-testid="tab-watching"
              className="data-[state=active]:bg-indigo-600"
            >
              Kijkend ({watchingCount})
            </TabsTrigger>
            <TabsTrigger 
              value="watchlist" 
              data-testid="tab-watchlist"
              className="data-[state=active]:bg-indigo-600"
            >
              Watchlist ({watchlistCount})
            </TabsTrigger>
            <TabsTrigger 
              value="completed" 
              data-testid="tab-completed"
              className="data-[state=active]:bg-indigo-600"
            >
              Voltooid ({completedCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-48 bg-white/5 border-white/10">
              <SelectValue placeholder="Sorteren op" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="progress">Voortgang</SelectItem>
              <SelectItem value="added_at">Laatst toegevoegd</SelectItem>
              <SelectItem value="title">Titel (A-Z)</SelectItem>
              <SelectItem value="episodes_left">Nog te kijken</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
            className="bg-white/5 border border-white/10 hover:bg-white/10"
            title={sortDir === 'asc' ? 'Oplopend' : 'Aflopend'}
          >
            {sortDir === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Series List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : sortedSeries.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sortedSeries.map((item) => (
            <SeriesCard 
              key={item.id} 
              series={item} 
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Tv className="w-16 h-16 text-gray-700 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">
            {activeTab === 'all' 
              ? 'Je hebt nog geen series toegevoegd'
              : activeTab === 'watching'
              ? 'Je kijkt momenteel geen series'
              : activeTab === 'watchlist'
              ? 'Je watchlist is leeg'
              : 'Je hebt nog geen series voltooid'}
          </p>
          <Link to="/search?type=tv">
            <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Series zoeken
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default SeriesPage;
